import { createAsyncThunk } from "@reduxjs/toolkit";
import localhost, {
  downloadFileFromResponse,
  fileNameFromResponse,
} from "apis/localhost";
import { MULTIPART_FORM_DATA_CONTENT_TYPE } from "constants/app";
import {
  addToJobQueue,
  disableNavBarAndSideBar,
  removeFromJobQueue,
} from "slices/app/appSlice";
import { selectShreddingTemplateEdit } from "slices/settings/selectors/shreddingTemplatesSelectors";
import { toUpdateRequest } from "slices/settings/shreddingTemplatesRequest";

const SHREDDING_TEMPLATES_URL = "/api/shredding-templates";
const EXPORT_FILE_TYPE = "application/json";
const DEFAULT_EXPORT_FILE_NAME = "template.json";

const errorBody = async (err) => {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text());
    } catch {
      return null;
    }
  }
  return data ?? null;
};

export const fetchShreddingTemplates = createAsyncThunk(
  "shreddingTemplates/fetch",
  async () => {
    const { data } = await localhost.get(SHREDDING_TEMPLATES_URL);
    return data ?? [];
  }
);

export const createShreddingTemplate = createAsyncThunk(
  "shreddingTemplates/create",
  async ({ name, description, copyFromTemplateId }, { rejectWithValue }) => {
    try {
      const { data } = await localhost.post(SHREDDING_TEMPLATES_URL, {
        name,
        description,
        copyFromTemplateId,
      });
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data ?? null);
    }
  }
);

export const fetchShreddingTemplate = createAsyncThunk(
  "shreddingTemplates/fetchOne",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await localhost.get(`${SHREDDING_TEMPLATES_URL}/${id}`);
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data ?? null);
    }
  }
);

export const lockShreddingTemplate = createAsyncThunk(
  "shreddingTemplates/lock",
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await localhost.post(
        `${SHREDDING_TEMPLATES_URL}/${id}/lock`
      );
      dispatch(disableNavBarAndSideBar(true));
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data ?? null);
    }
  }
);

export const unlockShreddingTemplate = createAsyncThunk(
  "shreddingTemplates/unlock",
  async (id, { dispatch, rejectWithValue }) => {
    dispatch(disableNavBarAndSideBar(false));
    try {
      await localhost.post(`${SHREDDING_TEMPLATES_URL}/${id}/unlock`);
      return id;
    } catch (err) {
      return rejectWithValue(err?.response?.data ?? null);
    }
  }
);

export const updateShreddingTemplate = createAsyncThunk(
  "shreddingTemplates/update",
  async (draft, { dispatch, rejectWithValue }) => {
    dispatch(addToJobQueue("updateShreddingTemplate"));

    try {
      const { data } = await localhost.put(
        `${SHREDDING_TEMPLATES_URL}/${draft.id}`,
        toUpdateRequest(draft)
      );
      dispatch(disableNavBarAndSideBar(false));
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data ?? null);
    } finally {
      dispatch(removeFromJobQueue("updateShreddingTemplate"));
    }
  }
);

export const duplicateShreddingTemplate = createAsyncThunk(
  "shreddingTemplates/duplicate",
  async (id, { dispatch, rejectWithValue }) => {
    dispatch(addToJobQueue("duplicateShreddingTemplate"));
    try {
      const { data } = await localhost.post(
        `${SHREDDING_TEMPLATES_URL}/${id}/duplicate`
      );
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data ?? null);
    } finally {
      dispatch(removeFromJobQueue("duplicateShreddingTemplate"));
    }
  }
);

export const deleteShreddingTemplate = createAsyncThunk(
  "shreddingTemplates/delete",
  async (id, { dispatch, rejectWithValue }) => {
    dispatch(addToJobQueue("deleteShreddingTemplate"));
    try {
      await localhost.delete(`${SHREDDING_TEMPLATES_URL}/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err?.response?.data ?? null);
    } finally {
      dispatch(removeFromJobQueue("deleteShreddingTemplate"));
    }
  }
);

export const exportShreddingTemplate = createAsyncThunk(
  "shreddingTemplates/export",
  async ({ id }, { dispatch, rejectWithValue }) => {
    dispatch(addToJobQueue("exportShreddingTemplate"));
    try {
      const response = await localhost.get(
        `${SHREDDING_TEMPLATES_URL}/${id}/export`,
        { responseType: "blob" }
      );
      downloadFileFromResponse(
        response.data,
        fileNameFromResponse(response, DEFAULT_EXPORT_FILE_NAME),
        { type: EXPORT_FILE_TYPE }
      );
      return id;
    } catch (err) {
      return rejectWithValue(await errorBody(err));
    } finally {
      dispatch(removeFromJobQueue("exportShreddingTemplate"));
    }
  }
);

export const importShreddingTemplate = createAsyncThunk(
  "shreddingTemplates/import",
  async (formData, { dispatch, rejectWithValue }) => {
    dispatch(addToJobQueue("importShreddingTemplate"));
    try {
      const { data } = await localhost.post(
        `${SHREDDING_TEMPLATES_URL}/import`,
        formData,
        { headers: { "Content-Type": MULTIPART_FORM_DATA_CONTENT_TYPE } }
      );
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data ?? null);
    } finally {
      dispatch(removeFromJobQueue("importShreddingTemplate"));
    }
  }
);

export const releaseShreddingTemplateEdit = () => (dispatch, getState) => {
  const edit = selectShreddingTemplateEdit(getState());
  if (edit) {
    dispatch(unlockShreddingTemplate(edit.templateId));
  }
};
